import { get, mapValues, reject, find, flatten, values, keys } from 'lodash';
import memoize from 'fast-memoize';
import { compose, curriedPickBy, curriedCountBy, prop, curriedMap, curriedFilter } from '@adjust/dashboard-core/lib/fp';

function KpiCohorts(gettext, $q, User, Account) {
  'ngInject';

  /**
   * @typedef {KpiList}
   * @type {Object}
   */
  const _cohortKpis = {
    all: [
      // users
      {
        isGroup: true,
        name: gettext('label.users'),
        token: 'group-users'
      },
      {
        group: 'group-users',
        fullName: gettext('kpi-cohorts.cohort_size'),
        name: gettext('kpi-cohorts.cohort_size_short'),
        token: 'cohort_size'
      },
      {
        group: 'group-users',
        fullName: gettext('kpi-cohorts.retained_users'),
        name: gettext('kpi-cohorts.retained_users_short'),
        token: 'retained_users'
      },
      {
        group: 'group-users',
        fullName: gettext('kpi-cohorts.retention_rate'),
        name: gettext('kpi-cohorts.retention_rate_short'),
        token: 'retention_rate',
        format: 'percent'
      },
      {
        group: 'group-users',
        fullName: gettext('kpi-cohorts.paying_users'),
        name: gettext('kpi-cohorts.paying_users_short'),
        token: 'paying_users'
      },
      {
        group: 'group-users',
        fullName: gettext('kpi-cohorts.paying_user_size'),
        name: gettext('kpi-cohorts.paying_user_size_short'),
        token: 'paying_user_size'
      },
      {
        group: 'group-users',
        fullName: gettext('kpi-cohorts.paying_users_retention_rate'),
        name: gettext('kpi-cohorts.paying_users_retention_rate_short'),
        token: 'paying_users_retention_rate',
        format: 'percent'
      },
      {
        group: 'group-users',
        fullName: gettext('kpi-cohorts.reattributions'),
        name: gettext('kpi-cohorts.reattributions_short'),
        token: 'reattributions'
      },
      {
        group: 'group-users',
        fullName: gettext('kpi-cohorts.deattributions'),
        name: gettext('kpi-cohorts.deattributions_short'),
        token: 'deattributions'
      },
      // sessions
      {
        isGroup: true,
        name: gettext('label.sessions'),
        token: 'group-sessions'
      },
      {
        group: 'group-sessions',
        fullName: gettext('kpi-cohorts.sessions'),
        name: gettext('kpi-cohorts.sessions_short'),
        token: 'sessions'
      },
      {
        group: 'group-sessions',
        fullName: gettext('kpi-cohorts.sessions_per_user'),
        name: gettext('kpi-cohorts.sessions_per_user_short'),
        token: 'sessions_per_user',
        format: 'number'
      },
      // revenue
      {
        isGroup: true,
        name: gettext('label.revenue'),
        token: 'group-revenue'
      },
      {
        group: 'group-revenue',
        fullName: gettext('kpi-cohorts.revenue'),
        name: gettext('kpi-cohorts.revenue_short'),
        token: 'revenue',
        format: 'currency'
      },
      {
        group: 'group-revenue',
        fullName: gettext('kpi-cohorts.revenue_total'),
        name: gettext('kpi-cohorts.revenue_total_short'),
        token: 'revenue_total',
        format: 'currency'
      },
      {
        group: 'group-revenue',
        fullName: gettext('kpi-cohorts.revenue_per_user'),
        name: gettext('kpi-cohorts.revenue_per_user_short'),
        token: 'revenue_per_user',
        format: 'currency'
      },
      {
        group: 'group-revenue',
        fullName: gettext('kpi-cohorts.revenue_per_paying_user'),
        name: gettext('kpi-cohorts.revenue_per_paying_user_short'),
        token: 'revenue_per_paying_user',
        format: 'currency'
      },
      {
        group: 'group-revenue',
        fullName: gettext('kpi-cohorts.lifetime_value'),
        name: gettext('kpi-cohorts.lifetime_value_short'),
        token: 'lifetime_value',
        format: 'currency'
      },
      {
        group: 'group-revenue',
        fullName: gettext('kpi-cohorts.paying_user_lifetime_value'),
        name: gettext('kpi-cohorts.paying_user_lifetime_value_short'),
        token: 'paying_user_lifetime_value',
        format: 'currency'
      },
      {
        group: 'group-revenue',
        fullName: gettext('kpi-cohorts.ad_revenue'),
        name: gettext('kpi-cohorts.ad_revenue'),
        token: 'ad_revenue',
        format: 'currency'
      },
      {
        group: 'group-revenue',
        fullName: gettext('kpi-cohorts.ad_revenue_total'),
        name: gettext('kpi-cohorts.ad_revenue_total'),
        token: 'ad_revenue_total',
        format: 'currency'
      },
      {
        group: 'group-revenue',
        fullName: gettext('kpi-cohorts.ad_rpm'),
        name: gettext('kpi-cohorts.ad_rpm_short'),
        token: 'ad_rpm',
        format: 'currency'
      },
      {
        group: 'group-revenue',
        fullName: gettext('kpi-cohorts.all_revenue'),
        name: gettext('kpi-cohorts.all_revenue'),
        token: 'all_revenue',
        format: 'currency'
      },
      {
        group: 'group-revenue',
        fullName: gettext('kpi-cohorts.all_revenue_total'),
        name: gettext('kpi-cohorts.all_revenue_total'),
        token: 'all_revenue_total',
        format: 'currency'
      },
      {
        group: 'group-revenue',
        fullName: gettext('kpi-cohorts.all_revenue_per_user'),
        name: gettext('kpi-cohorts.all_revenue_per_user'),
        token: 'all_revenue_per_user',
        format: 'currency'
      },
      // revenue-events
      {
        isGroup: true,
        name: gettext('label.revenue-events'),
        token: 'group-revenue-events'
      },
      {
        group: 'group-revenue-events',
        fullName: gettext('kpi-cohorts.revenue_events'),
        name: gettext('kpi-cohorts.revenue_events_short'),
        token: 'revenue_events'
      },
      {
        group: 'group-revenue-events',
        fullName: gettext('kpi-cohorts.revenue_events_total'),
        name: gettext('kpi-cohorts.revenue_events_total_short'),
        token: 'revenue_events_total'
      },
      {
        group: 'group-revenue-events',
        fullName: gettext('kpi-cohorts.revenue_events_per_user'),
        name: gettext('kpi-cohorts.revenue_events_per_user_short'),
        token: 'revenue_events_per_user',
        format: 'number'
      },
      {
        group: 'group-revenue-events',
        fullName: gettext('kpi-cohorts.revenue_events_per_paying_user'),
        name: gettext('kpi-cohorts.revenue_per_paying_user_short'),
        token: 'revenue_events_per_paying_user',
        format: 'number'
      },
      {
        group: 'group-revenue-events',
        fullName: gettext('kpi-cohorts.revenue_events_per_active_user'),
        name: gettext('kpi-cohorts.revenue_events_per_active_user_short'),
        token: 'revenue_events_per_active_user',
        format: 'number'
      },
      // time-spent
      {
        isGroup: true,
        name: gettext('label.time-spent'),
        token: 'group-time-spent'
      },
      {
        group: 'group-time-spent',
        fullName: gettext('kpi-cohorts.time_spent'),
        name: gettext('kpi-cohorts.time_spent_short'),
        token: 'time_spent',
        format: 'timespan'
      },
      {
        group: 'group-time-spent',
        fullName: gettext('kpi-cohorts.time_spent_per_user'),
        name: gettext('kpi-cohorts.time_spent_per_user_short'),
        token: 'time_spent_per_user',
        format: 'timespan'
      },
      {
        group: 'group-time-spent',
        fullName: gettext('kpi-cohorts.time_spent_per_session'),
        name: gettext('kpi-cohorts.time_spent_per_session_short'),
        token: 'time_spent_per_session',
        format: 'timespan'
      },
      // Uninstalls
      {
        isGroup: true,
        name: gettext('label.uninstalls'),
        token: 'group-uninstalls'
      },
      {
        group: 'group-uninstalls',
        fullName: gettext('kpi-cohorts.uninstalls'),
        name: gettext('kpi-cohorts.uninstalls_short'),
        token: 'uninstalls',
      },
      {
        group: 'group-uninstalls',
        fullName: gettext('kpi-cohorts.uninstalls_total'),
        name: gettext('kpi-cohorts.uninstalls_total_short'),
        token: 'uninstalls_total'
      },
      // Reinstalls
      {
        isGroup: true,
        name: gettext('label.reinstalls'),
        token: 'group-reinstalls'
      },
      {
        group: 'group-reinstalls',
        fullName: gettext('kpi-cohorts.reinstalls'),
        name: gettext('kpi-cohorts.reinstalls_short'),
        token: 'reinstalls',
      },
      {
        group: 'group-reinstalls',
        fullName: gettext('kpi-cohorts.reinstalls_total'),
        name: gettext('kpi-cohorts.reinstalls_total_short'),
        token: 'reinstalls_total'
      }
    ],
    events: [
      // users
      {
        isGroup: true,
        name: gettext('label.users'),
        token: 'group-users'
      },
      {
        group: 'group-users',
        fullName: gettext('kpi-cohorts.converted_users'),
        name: gettext('kpi-cohorts.converted_users_short'),
        token: 'converted_users'
      },
      {
        group: 'group-users',
        fullName: gettext('kpi-cohorts.conversion_distribution'),
        name: gettext('kpi-cohorts.conversion_distribution_short'),
        token: 'conversion_distribution',
        format: 'percent'
      },
      {
        group: 'group-users',
        fullName: gettext('kpi-cohorts.conversion_per_user'),
        name: gettext('kpi-cohorts.conversion_per_user_short'),
        token: 'conversion_per_user',
        format: 'number'
      },
      // events
      {
        isGroup: true,
        name: gettext('label.events'),
        token: 'group-events'
      },
      {
        group: 'group-events',
        fullName: gettext('kpi-cohorts.events'),
        name: gettext('kpi-cohorts.events_short'),
        token: 'events'
      },
      {
        group: 'group-events',
        fullName: gettext('kpi-cohorts.events_per_user'),
        name: gettext('kpi-cohorts.events_per_user_short'),
        token: 'events_per_user',
        format: 'number'
      },
      // revenue
      {
        isGroup: true,
        name: gettext('label.revenue'),
        token: 'group-revenue'
      },
      {
        group: 'group-revenue',
        fullName: gettext('kpi-cohorts.revenue'),
        name: gettext('kpi-cohorts.revenue_short'),
        token: 'revenue',
        format: 'currency'
      },
      {
        group: 'group-revenue',
        fullName: gettext('kpi-cohorts.revenue_total'),
        name: gettext('kpi-cohorts.revenue_total_short'),
        token: 'revenue_total',
        format: 'currency'
      },
      {
        group: 'group-revenue',
        fullName: gettext('kpi-cohorts.revenue_per_user'),
        name: gettext('kpi-cohorts.revenue_per_user_short'),
        token: 'revenue_per_user',
        format: 'currency'
      }
    ]
  };

  /**
   * Filters the available cohorts to keep only the subset that matches with
   * a set of conditions like: attribution source, current plan, permissions and
   * features of current user/account.
   *
   * @param {String} attributionSource
   * @param {User} user
   * @param {Account} account
   * @returns {KpiList}
   * @private
   */
  const _filterKpis = memoize((attributionSource, user, account) => compose(
      _filterEmptyGroups,
      _filterDeattributionKpi(attributionSource),
      _filterUninstallKpis(account),
      _filterMixedRevenueKpis(user, account),
      _filterAdRevenueKpis(user, account),
      _filterRevenueDataKpis(user)
    )(_cohortKpis));

  return {
    getAllAvailable,
    getDetails,
    _cohortKpis,
    _filterKpis,
    _filterRevenueDataKpis,
    _filterUninstallKpis,
    _filterAdRevenueKpis,
    _filterMixedRevenueKpis,
    _filterDeattributionKpi,
    _filterEmptyGroups,
    _getOrphanedGroups,
    _getFlatList,
    _getReferencedGroups,
    _removeKpisWhen,
    _removeKpis
  };

  /**
   * Gets all available cohort kpis
   *
   * @param {String} attributionSource
   * @returns {Promise<KpiList>}
   */
  function getAllAvailable(attributionSource) {
    return $q
      .all([User.getCurrent(), Account.getCurrent()])
      .then(([user, account]) => _filterKpis(attributionSource, user, account));
  }

  /**
   * Removes revenue kpis when the user doesn't have access to `revenue_data`
   *
   * @sig User -> KpiList -> KpiList
   * @param {User} user
   * @param {KpiList} kpis
   * @returns {KpiList}
   * @private
   */
  function _filterRevenueDataKpis(user) {
    const relatedKpis = [
      'revenue',
      'revenue_total',
      'revenue_per_user',
      'lifetime_value',
      'revenue_per_paying_user',
      'paying_user_lifetime_value',
      'group-revenue-events',
      'revenue_events',
      'revenue_events_per_user',
      'revenue_events_per_paying_user',
      'revenue_events_per_active_user',
      'revenue_events_total'
    ];

    const condition = !get(user, 'features.revenue_data.enabled', false);
    return _removeKpisWhen(condition, relatedKpis);
  }

  /**
   * Removes uninstall/reinstall kpis when the feature is not enabled
   * for the account.
   *
   * @sig Account -> KpiList -> KpiList
   * @param {Account} account
   * @param {KpiList} kpis
   * @returns {KpiList}
   * @private
   */
  function _filterUninstallKpis(account) {
    const relatedKpis = [
      'group-uninstalls',
      'uninstalls_total',
      'uninstalls',
      'group-reinstalls',
      'reinstalls_total',
      'reinstalls'
    ];

    const condition = !Account.hasUninstallPackage(account);
    return _removeKpisWhen(condition, relatedKpis);
  }

  /**
   * Removes ad revenue kpis when the feature is not enabled for the account
   * or the user has no access to `ad_revenue`
   *
   * @sig (User, Account) -> KpiList -> KpiList
   * @param {User} user
   * @param {Account} account
   * @param {KpiList} kpis
   * @returns {KpiList}
   * @private
   */
  function _filterAdRevenueKpis(user, account) {
    const relatedKpis = [
      'ad_revenue',
      'ad_revenue_total',
      'ad_rpm'
    ];

    const condition = !(
      User.featureIsEnabled(user, 'ad_revenue') &&
      Account.hasAdRevenuePackage(account, 'ad_revenue')
    );
    return _removeKpisWhen(condition, relatedKpis);
  }

  /**
   * Removes kpis that belong to ad revenue and revenue data in case either of
   * the features are not enabled for the account or the user has no access
   * to `ad_revenue`
   *
   * @sig (User, Account) -> KpiList -> KpiList
   * @param {User} user
   * @param {Account} account
   * @param {KpiList} kpis
   * @returns {KpiList}
   * @private
   */
  function _filterMixedRevenueKpis(user, account) {
    const relatedKpis = [
      'all_revenue',
      'all_revenue_total',
      'all_revenue_per_user'
    ];

    const condition = !(
      get(user, 'features.revenue_data.enabled', false) &&
      User.featureIsEnabled(user, 'ad_revenue') &&
      Account.hasAdRevenuePackage(account, 'ad_revenue')
    );
    return _removeKpisWhen(condition, relatedKpis);
  }

  /**
   * Removes the `deattributions` kpi when the attribution source is dynamic
   *
   * @sig String -> KpiList -> KpiList
   * @param {String} attributionSource
   * @param {KpiList} kpis
   * @returns {KpiList}
   * @private
   */
  function _filterDeattributionKpi(attributionSource) {
    const relatedKpis = [
      'deattributions'
    ];

    const condition = attributionSource === 'dynamic';
    return _removeKpisWhen(condition, relatedKpis);
  }

  /**
   * Removes any group that was not referenced by any kpi
   *
   * @param {KpiList} kpis
   * @returns {KpiList}
   * @private
   */
  function _filterEmptyGroups(kpis) {
    const toRemove = _getOrphanedGroups(kpis);
    return _removeKpis(kpis, toRemove);
  }

  /**
   * Returns a list of the orphaned groups that are not referenced by any kpi
   *
   * @param {KpiList} kpis
   * @returns {[String]}
   * @private
   */
  function _getOrphanedGroups(kpis) {
    const flatList = _getFlatList(kpis);
    const referenced = _getReferencedGroups(flatList);

    return compose(
      curriedMap(prop('token')),
      curriedFilter(item => item.isGroup && !referenced.includes(item.token))
    )(flatList);
  }

  /**
   * Flattens the list of kpis
   *
   * @param {KpiList} kpis
   * @returns {[Kpi]}
   * @private
   */
  function _getFlatList(kpis) {
    return compose(
      flatten,
      values
    )(kpis);
  }

    /**
   * Returns a list of the groups that are referenced at least once
   *
   * @param {[Kpi]} flatList
   * @returns {[String]}
   * @private
   */
  function _getReferencedGroups(flatList) {
    return compose(
      keys,
      curriedPickBy(sum => sum > 0),
      curriedCountBy(prop('group')),
      curriedFilter(item => !item.isGroup)
    )(flatList);
  }

  /**
   * Removes a subset of kpis from the provided list of kpis based on a condition
   *
   * @sig (Boolean, Array) -> KpiList -> KpiList
   * @param {Boolean} condition
   * @param {Array} toRemove
   * @param {KpiList} kpis
   * @returns {KpiList}
   * @private
   */
  function _removeKpisWhen(condition, toRemove) {
    return function(kpis) {
      return condition ?
        _removeKpis(kpis, toRemove) :
        kpis;
    };
  }

  /**
   * Removes the specified kpis from each section of the kpi list
   *
   * @param {KpiList} kpis
   * @param {Array} toRemove
   * @returns {KpiList}
   * @private
   */
  function _removeKpis(kpis, toRemove) {
    return mapValues(kpis, section => reject(section, kpi => toRemove.includes(kpi.token)));
  }

  /**
   * Get details for particular cohort kpi
   *
   * @param {String} kpiToken
   * @returns {Object|Undefined}
   */
  function getDetails(kpiToken) {
    const fullList = _cohortKpis.all.concat(_cohortKpis.events);
    return find(fullList, {token: kpiToken});
  }
}

export { KpiCohorts };
